import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLastPrescriptions } from "./api/prescription";

const PrescriptionForm = ({
    onSubmit,
    existingPrescriptions,
    onCancel,
    appId,
    patient,
    doctor,
    medicines,
}) => {
    // State for drug categories
    const [lastPrescriptions, setLastPrescriptions] = useState([]);
    const [recentDrugs, setRecentDrugs] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeInputIndex, setActiveInputIndex] = useState(null);
    const { data } = useLastPrescriptions(patient);
    // Refs for dropdown management
    const dropdownRef = useRef(null);
    const inputRefs = useRef([]);

    // Load data when component mounts
    useEffect(() => {
        loadLastPrescriptions();
        loadRecentDrugs();
    }, []);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !inputRefs.current.some(
                    (ref) => ref && ref.contains(event.target)
                )
            ) {
                setShowDropdown(false);
                setActiveInputIndex(null);
                setSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Load last prescriptions for this patient
    const loadLastPrescriptions = async () => {
        try {
            // Get from localStorage or use mock data
            // Default mock data
            let mockData =
                data &&
                data?.prescriptions.map((d, index) => {
                    return {
                        id: index,
                        name: d?.drug,
                        dosage: d.dosage,
                        drugType: d?.drugType,
                        duration: d?.durationNumber + " " + d?.durationUnit,
                        instruction: d?.instruction,
                    };
                });

            setLastPrescriptions(mockData);
        } catch (error) {
            console.error("Error loading last prescriptions:", error);
            // Fallback mock data
            setLastPrescriptions([]);
        }
    };

    // Load recent drugs based on doctor's usage frequency
    const loadRecentDrugs = async () => {
        try {
            // Get from localStorage or use mock data
            const storedData = localStorage.getItem(
                `recentDrugs_${doctor?.id || "default"}`
            );
            let mockData = [];

            if (storedData) {
                mockData = JSON.parse(storedData);
            } else {
                // Default mock data
                mockData = [
                    {
                        id: 1,
                        name: "TABLET Mirtasys MD (15+30 mg)",
                        usageCount: 12,
                    },
                    {
                        id: 2,
                        name: "TABLET Arifine (5 mg)",
                        usageCount: 8,
                    },
                    {
                        id: 3,
                        name: "TABLET Voxox (100 mg)",
                        usageCount: 6,
                    },
                ];
            }

            // Sort by usage count (most used first)
            mockData.sort((a, b) => b.usageCount - a.usageCount);
            setRecentDrugs(mockData);
        } catch (error) {
            console.error("Error loading recent drugs:", error);
            // Fallback mock data
            setRecentDrugs([
                {
                    id: 1,
                    name: "TABLET Mirtasys MD (15+30 mg)",
                    usageCount: 12,
                },
                {
                    id: 2,
                    name: "TABLET Arifine (5 mg)",
                    usageCount: 8,
                },
                {
                    id: 3,
                    name: "TABLET Voxox (100 mg)",
                    usageCount: 6,
                },
            ]);
        }
    };

    const updateLastPrescriptions = (newPrescription) => {
        const prescriptionData = {
            id: Date.now(),
            name: newPrescription.drug,
            dosage: newPrescription.dosage,
            duration: `${newPrescription.durationNumber} ${newPrescription.durationUnit}`,
            instruction: newPrescription.instruction,
        };

        setLastPrescriptions((prev) => {
            const existingIndex = prev.findIndex(
                (p) => p.name === newPrescription.drug
            );
            let updated;

            if (existingIndex !== -1) {
                // Update existing prescription
                updated = [...prev];
                updated[existingIndex] = prescriptionData;
            } else {
                // Add new prescription at the beginning (most recent first)
                updated = [prescriptionData, ...prev.slice(0, 9)]; // Keep only 10 most recent
            }

            // Save to localStorage
            try {
                localStorage.setItem(
                    `lastPrescriptions_${patient?.id || "default"}`,
                    JSON.stringify(updated)
                );
            } catch (error) {
                console.error("Error saving last prescriptions:", error);
            }

            return updated;
        });
    };

    // Update recent drugs usage count
    const updateRecentDrugs = (drugName) => {
        setRecentDrugs((prev) => {
            const existingIndex = prev.findIndex((d) => d.name === drugName);
            let updated;

            if (existingIndex !== -1) {
                // Increase usage count for existing drug
                updated = [...prev];
                updated[existingIndex].usageCount += 1;
            } else {
                // Add new drug with count 1
                const newDrug = {
                    id: Date.now(),
                    name: drugName,
                    usageCount: 1,
                };
                updated = [newDrug, ...prev];
            }

            // Sort by usage count (most used first) and keep only top 10
            updated = updated
                .sort((a, b) => b.usageCount - a.usageCount)
                .slice(0, 10);

            // Save to localStorage
            try {
                localStorage.setItem(
                    `recentDrugs_${doctor?.id || "default"}`,
                    JSON.stringify(updated)
                );
            } catch (error) {
                console.error("Error saving recent drugs:", error);
            }

            return updated;
        });
    };

    // Parse dosage string helper
    const parseDosage = (dosageString) => {
        if (!dosageString) return { morning: "0", afternoon: "0", night: "0" };
        const parts = dosageString.split("-");
        return {
            morning: parts[0] || "0",
            afternoon: parts[1] || "0",
            night: parts[2] || "0",
        };
    };

    // Initialize prescriptions
    const initializePrescriptions = () => {
        if (existingPrescriptions && existingPrescriptions?.length > 0) {
            return existingPrescriptions.map((pres) => {
                const parsedDosage = parseDosage(pres.dosage);
                return { ...pres, ...parsedDosage };
            });
        }
        return [
            {
                drug: "",
                morning: "0",
                afternoon: "0",
                night: "0",
                dosage: "",
                durationNumber: "",
                durationUnit: "days",
                instruction: "",
                quantity: 0,
                drugType: "",
                doctor,
                patient,
            },
        ];
    };

    const [prescriptions, setPrescriptions] = useState(
        initializePrescriptions()
    );

    const handleInputChange = (index, field, value) => {
        const newPrescriptions = [...prescriptions];
        newPrescriptions[index][field] = value;

        // Update dosage string
        if (field === "morning" || field === "afternoon" || field === "night") {
            const morning =
                field === "morning" ? value : newPrescriptions[index].morning;
            const afternoon =
                field === "afternoon"
                    ? value
                    : newPrescriptions[index].afternoon;
            const night =
                field === "night" ? value : newPrescriptions[index].night;
            newPrescriptions[index].dosage = `${morning}-${afternoon}-${night}`;
        }

        // Auto-calculate quantity
        if (
            field === "morning" ||
            field === "afternoon" ||
            field === "night" ||
            field === "durationNumber" ||
            field === "durationUnit"
        ) {
            const dosage = newPrescriptions[index].dosage;
            const durationNumber = newPrescriptions[index].durationNumber;
            const durationUnit = newPrescriptions[index].durationUnit;

            if (dosage && durationNumber) {
                const calculatedQuant = calculateQuantity(
                    dosage,
                    `${durationNumber} ${durationUnit}`
                );

                newPrescriptions[index].quantity =
                    !newPrescriptions[index].quantity ||
                    calculatedQuant !== newPrescriptions[index].quantity
                        ? calculateQuantity(
                              dosage,
                              `${durationNumber} ${durationUnit}`
                          )
                        : newPrescriptions[index].quantity;
            }
        }

        setPrescriptions(newPrescriptions);
    };

    const calculateQuantity = (dosageStr, durationStr) => {
        if (!dosageStr || !durationStr) return 0;

        // Convert fractional strings like "½" or "1½" into float
        const dosage = dosageStr.split("-").reduce((sum, part) => {
            // Handle "½", "1½", etc.
            if (part.includes("½")) {
                const [whole = "0"] = part.split("½"); // extract whole part before "½"
                return sum + parseInt(whole || "0") + 0.5;
            }
            return sum + (parseFloat(part) || 0);
        }, 0);

        const match = durationStr.match(/(\d+)\s*(weeks|months|days)/);
        if (!match) return 0;

        const [, num, unit] = match;
        const duration =
            unit === "weeks" ? num * 7 : unit === "months" ? num * 30 : num;
        return dosage * duration;
    };

    const addMedicine = () => {
        setPrescriptions([
            ...prescriptions,
            {
                drug: "",
                morning: "0",
                afternoon: "0",
                night: "0",
                dosage: "",
                durationNumber: "",
                durationUnit: "days",
                instruction: "",
                quantity: 0,
                drugType: "",
                patient,
                doctor,
            },
        ]);
    };

    const removeMedicine = (index) => {
        if (prescriptions?.length > 1) {
            setPrescriptions(prescriptions.filter((_, i) => i !== index));
        }
    };

    const handleSubmit = () => {
        const validPrescriptions = prescriptions.filter(
            (p) => p.drug && p.durationNumber
        );

        if (validPrescriptions?.length > 0) {
            // Update tracking data for each prescription
            validPrescriptions.forEach((prescription) => {
                updateLastPrescriptions(prescription);
                updateRecentDrugs(prescription.drug);
            });

            onSubmit(validPrescriptions, existingPrescriptions);
        }
    };

    const handleDrugSelect = (index, drugData, category = null) => {
        const drugName =
            typeof drugData === "string" ? drugData : drugData.name;
        handleInputChange(index, "drug", drugName);
        handleInputChange(index, "drugType", drugData?.type);

        // Auto-fill from last prescription
        if (category === "lastPrescription") {
            const lastPres = lastPrescriptions.find((p) => p.name === drugName);
            if (lastPres) {
                const parsedDosage = parseDosage(lastPres.dosage);
                handleInputChange(index, "morning", parsedDosage.morning);
                handleInputChange(index, "afternoon", parsedDosage.afternoon);
                handleInputChange(index, "night", parsedDosage.night);

                const durationMatch = lastPres.duration.match(
                    /(\d+)\s*(days|weeks|months)/
                );
                if (durationMatch) {
                    handleInputChange(
                        index,
                        "durationNumber",
                        durationMatch[1]
                    );
                    handleInputChange(index, "durationUnit", durationMatch[2]);
                }

                if (lastPres.instruction) {
                    handleInputChange(
                        index,
                        "instruction",
                        lastPres.instruction
                    );
                }
            }
        }

        // Update recent drugs count when a drug is selected
        updateRecentDrugs(drugName);

        setShowDropdown(false);
        setActiveInputIndex(null);
        setSearchTerm("");
    };

    const filterItems = (items, term) => {
        if (!term) return items;
        return items.filter((item) => {
            const name = item.name || "";
            return name.toLowerCase().includes(term.toLowerCase());
        });
    };
    useEffect(() => {
        if (data?.prescriptions?.length) {
            loadLastPrescriptions(data.prescriptions);
        } else {
            console.warn(
                "No last prescription data found for patient:",
                patient?.id
            );
            setLastPrescriptions([]); // Clear fallback if needed
        }
    }, [data, patient]);

    const handleLastPrescription = (e) => {
        e.preventDefault();

        if (lastPrescriptions?.length) {
            const transformed = lastPrescriptions.map((p) => {
                const parsedDosage = parseDosage(p.dosage);
                const durationMatch = p.duration?.match(
                    /(\d+)\s*(days|weeks|months)/
                );

                return {
                    drug: p.name || "",
                    morning: parsedDosage.morning,
                    afternoon: parsedDosage.afternoon,
                    night: parsedDosage.night,
                    dosage: p.dosage || "",
                    durationNumber: durationMatch?.[1] || "",
                    durationUnit: durationMatch?.[2] || "days",
                    instruction: p.instruction || "",
                    quantity: p.quantity,
                    drugType: p?.drugType || "",
                    doctor,
                    patient,
                };
            });

            setPrescriptions(transformed);

            // Optionally auto-submit after loading all last prescriptions:
            // handleSubmit();
        }

        setShowDropdown(false);
        setActiveInputIndex(null);
        setSearchTerm("");
    };

    const renderDrugDropdown = (index) => {
        if (activeInputIndex !== index || !showDropdown) return null;

        const filteredMedicines = medicines
            ? filterItems(medicines, searchTerm)
            : [];
        const filteredLastPrescriptions = filterItems(
            lastPrescriptions,
            searchTerm
        );
        const filteredRecentDrugs = filterItems(recentDrugs, searchTerm);

        return (
            <div
                ref={dropdownRef}
                style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
            >
                {/* Last Prescriptions */}
                {filteredLastPrescriptions?.length > 0 && (
                    <div>
                        <div
                            style={{
                                padding: "10px 12px",
                                backgroundColor: "white",
                                fontWeight: "600",
                                fontSize: "15px",
                                color: "black",
                                borderBottom: "1px solid #eee",
                            }}
                            onClick={handleLastPrescription}
                        >
                            Last Prescription
                        </div>
                        {filteredLastPrescriptions.map((drug, idx) => (
                            <div
                                key={`last-${drug.id || idx}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleDrugSelect(
                                        index,
                                        drug,
                                        "lastPrescription"
                                    );
                                }}
                                style={{
                                    padding: "12px 16px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    borderBottom: "1px solid #f5f5f5",
                                    transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = "#f8f9fa")
                                }
                                onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor = "white")
                                }
                            >
                                <div
                                    style={{
                                        fontWeight: "500",
                                        marginBottom: "4px",
                                    }}
                                >
                                    {drug.name}
                                </div>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                    }}
                                >
                                    {drug.dosage} • {drug.duration}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Recent Drugs */}
                {/* {filteredRecentDrugs?.length > 0 && (
          <div>
            <div style={{
              padding: '10px 12px', backgroundColor: 'white', fontWeight: '600',
              fontSize: '15px', color: 'black', borderBottom: '1px solid #eee'
            }}>
              Recent Drugs
            </div>
            {filteredRecentDrugs.map((drug, idx) => (
              <div
                key={`recent-${drug.id || idx}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleDrugSelect(index, drug, 'recentDrug');
                }}
                style={{
                  padding: '12px 16px', cursor: 'pointer', fontSize: '14px',
                  borderBottom: '1px solid #f5f5f5', transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>{drug.name}</div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  Used {drug.usageCount} times
                </div>
              </div>
            ))}
          </div>
        )} */}

                {/* All Medicines */}
                {filteredMedicines?.length > 0 && (
                    <div>
                        <div
                            style={{
                                padding: "10px 12px",
                                backgroundColor: "white",
                                fontWeight: "600",
                                fontSize: "15px",
                                color: "black",
                                borderBottom: "1px solid #eee",
                            }}
                        >
                            All Medicines ({filteredMedicines?.length})
                        </div>
                        {filteredMedicines.map((medicine, idx) => (
                            <div
                                key={`medicine-${medicine.id || idx}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleDrugSelect(index, medicine);
                                }}
                                style={{
                                    padding: "12px 16px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    borderBottom: "1px solid #f5f5f5",
                                    transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = "#f8f9fa")
                                }
                                onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor = "white")
                                }
                            >
                                <div style={{ fontWeight: "500" }}>
                                    {medicine.name}
                                </div>
                                {medicine.strength && (
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: "#6c757d",
                                            marginTop: "2px",
                                        }}
                                    >
                                        {medicine.strength}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* No results */}
                {filteredMedicines?.length === 0 &&
                    filteredLastPrescriptions?.length === 0 &&
                    filteredRecentDrugs?.length === 0 && (
                        <div
                            style={{
                                padding: "20px",
                                textAlign: "center",
                                color: "#6c757d",
                                fontSize: "14px",
                            }}
                        >
                            {searchTerm
                                ? `No medicines found matching "${searchTerm}"`
                                : "Start typing to search for medicines"}
                        </div>
                    )}
            </div>
        );
    };

    return (
        <div style={{ marginTop: "20px" }}>
            <table className="table table-bordered">
                <thead>
                    <tr>
                        <th style={{ width: "18%" }}>Drug</th>
                        <th style={{ width: "22%" }}>Dosage</th>
                        <th style={{ width: "18%" }}>Duration</th>
                        <th style={{ width: "18%" }}>Instructions</th>
                        <th style={{ width: "10%" }}>Quantity</th>
                        <th style={{ width: "14%" }}>Actions</th>
                    </tr>
                    <tr>
                        <th></th>
                        <th style={{ fontSize: "12px", padding: "5px" }}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>Morning</span>
                                <span>Afternoon</span>
                                <span>Night</span>
                            </div>
                        </th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {prescriptions.map((prescription, index) => (
                        <tr key={index}>
                            {/* Drug Column */}
                            <td style={{ position: "relative" }}>
                                <input
                                    ref={(el) =>
                                        (inputRefs.current[index] = el)
                                    }
                                    type="text"
                                    className="form-control"
                                    value={prescription.drug}
                                    onChange={(e) => {
                                        handleInputChange(
                                            index,
                                            "drug",
                                            e.target.value
                                        );
                                        setSearchTerm(e.target.value);
                                    }}
                                    onFocus={() => {
                                        setActiveInputIndex(index);
                                        setShowDropdown(true);
                                        setSearchTerm(prescription.drug);
                                    }}
                                    placeholder="Search or select drug..."
                                    style={{ width: "100%", padding: "8px" }}
                                />
                                {renderDrugDropdown(index)}
                            </td>

                            {/* Dosage Column */}
                            <td>
                                <div style={{ display: "flex", gap: "3px" }}>
                                    {["morning", "afternoon", "night"].map(
                                        (time) => (
                                            <select
                                                key={time}
                                                className="form-control"
                                                value={prescription[time]}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        index,
                                                        time,
                                                        e.target.value
                                                    )
                                                }
                                                style={{
                                                    width: "32%",
                                                    padding: "6px",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                <option value="0">0</option>
                                                <option value="½">½</option>
                                                <option value="1">1</option>
                                                <option value="1½">1½</option>
                                                <option value="2">2</option>
                                                <option value="2½">2½</option>
                                                <option value="3">3</option>
                                                <option value="3½">3½</option>
                                                <option value="4">4</option>
                                                <option value="4½">4½</option>
                                                <option value="5">5</option>
                                                <option value="5½">5½</option>
                                                <option value="6">6</option>
                                                <option value="6½">6½</option>
                                                <option value="7">7</option>
                                                <option value="7½">7½</option>
                                                <option value="8">8</option>
                                                <option value="8½">8½</option>
                                                <option value="9">9</option>
                                                <option value="9½">9½</option>
                                                <option value="10">10</option>
                                                <option value="10½">10½</option>
                                                <option value="11">11</option>
                                                <option value="11½">11½</option>
                                                <option value="12">12</option>
                                                <option value="12½">12½</option>
                                                <option value="13">13</option>
                                                <option value="13½">13½</option>
                                                <option value="14">14</option>
                                                <option value="14½">14½</option>
                                                <option value="15">15</option>
                                            </select>
                                        )
                                    )}
                                </div>
                            </td>

                            {/* Duration Column */}
                            <td>
                                <div style={{ display: "flex", gap: "5px" }}>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={prescription.durationNumber}
                                        onChange={(e) =>
                                            handleInputChange(
                                                index,
                                                "durationNumber",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Number"
                                        style={{ width: "60%", padding: "8px" }}
                                        min="1"
                                    />
                                    <select
                                        className="form-control"
                                        value={prescription.durationUnit}
                                        onChange={(e) =>
                                            handleInputChange(
                                                index,
                                                "durationUnit",
                                                e.target.value
                                            )
                                        }
                                        style={{ width: "40%", padding: "8px" }}
                                    >
                                        <option value="days">days</option>
                                        <option value="weeks">weeks</option>
                                        <option value="months">months</option>
                                    </select>
                                </div>
                            </td>

                            {/* Instructions Column */}
                            <td>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "4px",
                                    }}
                                >
                                    <select
                                        className="form-control"
                                        value={
                                            [
                                                "after food",
                                                "before food",
                                                "with food",
                                                "mix with food/liquid",
                                                "transdermal patch",
                                                "Local Application",
                                                "once in a week",
                                                "once in a month",
                                            ].includes(prescription.instruction)
                                                ? prescription.instruction
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                index,
                                                "instruction",
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: "100%",
                                            padding: "8px",
                                        }}
                                    >
                                        <option value="">
                                            Select Instruction
                                        </option>
                                        <option value="after food">
                                            After food
                                        </option>
                                        <option value="before food">
                                            Before food
                                        </option>
                                        <option value="with food">
                                            With food
                                        </option>
                                        <option value="mix with food/liquid">
                                            Mix with food/liquid
                                        </option>
                                        <option value="transdermal patch">
                                            Transdermal patch
                                        </option>
                                        <option value="Local Application">
                                            Local Application
                                        </option>
                                        <option value="once in a week">
                                            Once in a week
                                        </option>
                                        <option value="once in a month">
                                            Once in a month
                                        </option>
                                    </select>

                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Or type custom instruction"
                                        value={
                                            ![
                                                "after food",
                                                "before food",
                                                "with food",
                                                "mix with food/liquid",
                                                "transdermal patch",
                                                "Local Application",
                                                "once in a week",
                                                "once in a month",
                                            ].includes(prescription.instruction)
                                                ? prescription.instruction
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleInputChange(
                                                index,
                                                "instruction",
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            width: "100%",
                                            padding: "8px",
                                        }}
                                    />
                                </div>
                            </td>

                            {/* Quantity Column */}
                            <td>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={prescription.quantity}
                                    onChange={(e) =>
                                        handleInputChange(
                                            index,
                                            "quantity",
                                            parseInt(e.target.value)
                                        )
                                    }
                                    style={{ width: "100%", padding: "8px" }}
                                    min="0"
                                />
                            </td>

                            {/* Actions Column */}
                            <td>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "5px",
                                        justifyContent: "center",
                                    }}
                                >
                                    {prescriptions?.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeMedicine(index)
                                            }
                                            style={{ padding: "4px 8px" }}
                                        >
                                            ×
                                        </button>
                                    )}
                                    {index === prescriptions?.length - 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-success"
                                            onClick={addMedicine}
                                            style={{ padding: "4px 8px" }}
                                        >
                                            +
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Action Buttons */}
            <div
                style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                    marginTop: "15px",
                }}
            >
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    style={{ padding: "10px 20px" }}
                >
                    Save
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                    style={{ padding: "10px 20px" }}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default PrescriptionForm;
